/**
 * CR-6435: the customer-view Calendar filter card — the same shell as the
 * Deliveries, NCR and ASN panels. The header (title, Refresh, Export, Filters
 * toggle carrying the active count) stays visible when the panel is
 * collapsed; only the sections and the Clear all / Apply filters footer hide,
 * as the design note requires.
 */
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { X, Search, Filter, RefreshCw, Download } from 'lucide-react';

export interface CalendarFiltersPanelProps {
  expanded: boolean;
  onShowFilters: () => void;
  onHideFilters: () => void;
  onRefresh: () => void;
  onExport: () => void;
  onClear: () => void;
  onApply: () => void;
  /** Number of filters currently applied, shown as a badge in the header. */
  activeFilterCount: number;
  children: ReactNode;
}

export function CalendarFiltersPanel({
  expanded, onShowFilters, onHideFilters, onRefresh, onExport, onClear, onApply,
  activeFilterCount, children,
}: CalendarFiltersPanelProps) {
  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base text-xl">Filter Calendar</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={onExport}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export
            </Button>
            <button
              type="button"
              onClick={expanded ? onHideFilters : onShowFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  expanded
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4 p-0">
          {children}
          {/* Filter footer — Clear all left, Apply filters right. */}
          <div className="flex items-center justify-between px-6 pb-4">
            <Button size="sm" variant="outline" onClick={onClear}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Clear all
            </Button>
            <Button size="sm" onClick={onApply}>
              <Search className="h-3.5 w-3.5 mr-1.5" />
              Apply filters
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
